import { TeamMemberPriorityCard } from "@/components/team/TeamMemberPriorityCard";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { UserAvatar } from "@/components/UserAvatar";
import { cn } from "@/lib/utils";
import { OrgMember, Priority, TeamMemberUpdateStatus } from "@/types";
import { AlertTriangle, Target } from "lucide-react";
import { missingFocusQueueLabel, updateSignal, updateToneClass } from "./teamFilterConstants";

interface TeamMemberSectionProps {
  member: OrgMember;
  priorities: Priority[];
  memberUpdate?: TeamMemberUpdateStatus;
  isSelf: boolean;
  hasStatusFilter: boolean;
  onPriorityClick: (priority: Priority) => void;
}

export const TeamMemberSection = ({
  member,
  priorities,
  memberUpdate,
  isSelf,
  hasStatusFilter,
  onPriorityClick,
}: TeamMemberSectionProps) => {
  const signal = memberUpdate ? updateSignal(memberUpdate) : undefined;
  const missingLabel = memberUpdate ? missingFocusQueueLabel(memberUpdate) : undefined;

  return (
    <div className="flex flex-col overflow-hidden rounded-xl border border-border bg-card shadow-sm">
      {/* Team Member Header */}
      <div className="flex items-center gap-4 border-b border-border bg-muted/20 p-5 transition-colors hover:bg-muted/40">
        <UserAvatar
          src={member.avatarUrl}
          name={member.fullName}
          className="h-12 w-12 border-2 border-background shadow-sm"
        />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="truncate text-lg font-semibold text-foreground">{member.fullName}</h2>
            {isSelf && (
              <Badge variant="secondary" className="shrink-0">
                You
              </Badge>
            )}
            {missingLabel && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge
                    variant="outline"
                    className="shrink-0 gap-1 border-amber-500/30 bg-amber-500/10 text-amber-600"
                  >
                    <AlertTriangle className="h-3 w-3" />
                    {missingLabel}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  Every member should hold 1 Focus and 2 Queue items — this board is missing one or both.
                </TooltipContent>
              </Tooltip>
            )}
          </div>
          {/* One line carries the whole weekly state: the date IS the status, the colour is the
              verdict. No badge repeating in words what "9 days ago" already says. */}
          <div className="flex flex-wrap items-center gap-x-2 text-sm text-muted-foreground">
            {member.position && <span className="truncate">{member.position}</span>}
            {signal && (
              <>
                {member.position && <span aria-hidden>·</span>}
                <span className={cn("inline-flex items-center gap-1.5 font-medium", updateToneClass[signal.tone])}>
                  <span className="h-2 w-2 rounded-full bg-current" />
                  {signal.label}
                </span>
                {/* Only rendered for "gone completely quiet" — no report AND no activity this
                    week. Coloured like a flag, not muted context, because that's what it is. */}
                {signal.activityText && (
                  <>
                    <span aria-hidden>·</span>
                    <span className="font-medium text-destructive">
                      No activity either: {signal.activityText}
                    </span>
                  </>
                )}
              </>
            )}
          </div>
        </div>
        <div className="flex shrink-0 flex-col items-end">
          <Badge
            variant="secondary"
            className="gap-1.5 border-0 bg-primary/10 px-3 py-1 text-primary hover:bg-primary/20"
          >
            <Target className="h-3.5 w-3.5" />
            <span className="text-sm font-medium">{priorities.length}</span>
            <span className="hidden font-normal opacity-80 sm:inline">
              {priorities.length === 1 ? "Priority" : "Priorities"}
            </span>
          </Badge>
        </div>
      </div>

      {/* Priorities List */}
      <div className="flex-1 bg-background/50 p-5">
        {priorities.length > 0 ? (
          <div className="grid grid-cols-1 items-stretch gap-4 md:grid-cols-2 xl:grid-cols-3">
            {priorities.map((priority) => (
              <div key={priority.pid} className="h-full animate-in fade-in zoom-in-95 duration-300">
                <TeamMemberPriorityCard
                  priority={priority}
                  memberPid={member.pid}
                  onClick={() => onPriorityClick(priority)}
                />
              </div>
            ))}
          </div>
        ) : (
          <div className="flex min-h-[160px] flex-col items-center justify-center rounded-xl border border-dashed border-border/50 bg-muted/20 p-10 text-center">
            <div className="mb-3 rounded-full bg-muted p-3">
              <Target className="h-6 w-6 text-muted-foreground/60" />
            </div>
            <p className="text-sm font-medium text-muted-foreground">No active priorities found</p>
            <p className="mt-1 max-w-[250px] text-xs text-muted-foreground/70">
              {hasStatusFilter
                ? "Try adjusting your status filters to see more priorities."
                : "This team member currently has no assigned priorities."}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
